
import React, { useState, useEffect } from 'react';
import { Modal, Input, Row, Col, Button, Space, Avatar, Select, DatePicker, Tag, AutoComplete } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import './AddEdit.less';
import { Project, ProjectAddEditParam, DemandBrief } from '../model';  
import { SNumber, User } from '@/common/model';
import { Option } from 'antd/es/mentions';
import UserIcon from '@/components/UserIcon/UserIcon';
import UserSelect from '@/components/UserSelect/UserSelect';
import { globalStore } from '@/common';
import { ctrl } from '../controller';
import { PlusOutlined } from '@ant-design/icons';
import StatusSelect from '@/components/StatusSelect/StatusSelect';

interface AddEditProps {
  visible: boolean;
  onClose: () => void;
  project?: Project;
  currentUser?: User; 
  candidateDemands: DemandBrief[];
}

const AddEdit: React.FC<AddEditProps> = ({ visible, onClose, project, currentUser, candidateDemands }) => {
  const [name, setName] = useState(project ? project.name : '');
  const [businessId, setBusinessId] = useState(project ? project.businessId : null);
  const [content, setContent] = useState(project ? project.content : '');
  const [demandIds, setDemandIds] = useState(project ? project.demands.map(item => item.id) : []); 
  const [submitterId, setSubmitterId] = useState(project ? project.submitterId : currentUser ? currentUser.id : undefined); 
  const [directorId, setDirectorId] = useState(project ? project.directorId : undefined);
  const [status, setStatus] = useState(project ? project.status : 0); 
  const [deadline, setDeadline] = useState<Dayjs | null>(null);
  const [documentLink, setDocumentLink] = useState('');
  const [demandModalVisible, setDemandModalVisible] = useState(false);
  const [filteredDemands, setFilteredDemands] = useState([]);
  const [demandSearch, setDemandSearch] = useState('');

  if(!candidateDemands){
    console.log("传入的需求列表为空")
  }
  useEffect(() => {
    setName(project ? project.name : '');
    setBusinessId(project ? project.businessId : null);
    setDemandIds(project ? project.demands.map(item => item.id) : []);
    setDirectorId(project ? project.directorId : undefined); 
    setSubmitterId(project ? project.submitterId : currentUser ? currentUser.id : undefined);
    setContent(project ? project.content : '');
    setStatus(project ? project.status : 0);
    setDeadline(
      project?.finishTime ? dayjs(project.finishTime) : null,
    );
    setDocumentLink(project ? project.docLink : '');
  }, [project]);

  useEffect(()=>{
      if(!candidateDemands){
        return;
      }
      const filterResult = candidateDemands.filter(demand => 
        demand.content.includes(demandSearch) || String(demand.id).includes(demandSearch)
      );
      setFilteredDemands(filterResult);
  }, [demandSearch])

  const isEditing = !!project;

  if (!isEditing && !currentUser) {
    return null;
  }

  const handleDemandSelect = (id: SNumber) => {
    setDemandIds([...demandIds, id]);
    setDemandModalVisible(false);
  };

  const handleTagClose = (tag: SNumber) => {
    setDemandIds(demandIds.filter(id => id !== tag));
  };
 

  const handleConfirmAndAddOrUpdateProject = async () => {
    const tempProject: ProjectAddEditParam = { 
      id: isEditing ? project!.id : null,
      name,
      docLink:documentLink,
      submitterId, 
      finishDate: deadline ? deadline.toDate() : undefined,
      demandIds,
      content,
      directorId,
      status
    };
    const result = isEditing
      ? await ctrl.updateProject(tempProject)
      : await ctrl.addProject(tempProject);
    if (result >= 0) {
      onClose();
    }
  };

  return (
    <Modal
      title={isEditing ? '编辑项目' : '创建项目'}
      visible={visible}
      onCancel={onClose}
      footer={null}
      width={600}
      className='project-edit-modal'
    >
      <Row gutter={16} style={{ marginBottom: 20 }}>
        <Col span={24}>
          <Input 
            placeholder="项目名称"
            value={name}
            onChange={e => setName(e.target.value)}
            className='custom-input'
          />
        </Col>
      </Row>
      <Row gutter={16} style={{ marginBottom: 20 }}>
        <Col span={24}>
          <Input.TextArea 
            placeholder="描述项目的主要内容和目标"
            value={content}
            onChange={e => setContent(e.target.value)}
            rows={2}
            className='custom-input'
          />
        </Col>
      </Row>
      <Row gutter={16} style={{ marginBottom: 20 }}>
        <Col span={4}>
          <label className='attri-name'>关联需求:</label>
        </Col>
        <Col span={20} style={{lineHeight:'32px'}}> 
          <Space>
            {demandIds.map(id => (
              <Tag
                key={id}
                closable
                className='demand-item-id'
                onClose={() => handleTagClose(id)}
              >
                <span style={{width:80}}>#{id}</span>
              </Tag>
            ))}
          </Space>
          <Button
                  className="add-user-btn"
                  type="primary"
                  shape="circle"
                  size='small'
                  icon={<PlusOutlined />}
                  onClick={() => setDemandModalVisible(true)}
                /> 
        </Col>
      </Row>
      <Row gutter={16} style={{ marginBottom: 20 }}>
        <Col span={4}>
          <label className='attri-name'>文档链接:</label>
        </Col>
        <Col span={20}>
          <Input 
            placeholder="如果有项目文档，请把链接贴在这里"
            value={documentLink}
            onChange={e => setDocumentLink(e.target.value)}
            className='custom-input doc-link-input'
          />
        </Col>
      </Row> 
      <Row gutter={16} style={{ marginBottom: 20 }}>
        <Col span={4}>
           <label className='attri-name'>提出人员:</label>
        </Col>
        <Col span={8} style={{paddingTop:2}}>
           <UserIcon showName effect='post' id={submitterId+""} />
        </Col>
        <Col span={4}>
            <label className='attri-name'>主负责人:</label>
        </Col>
        <Col span={8} >
            <UserSelect
                userMap={globalStore.userMap}
                mode="single"
                value={directorId}
                onMultipleSelect={() => {}}
                onSingleSelect={(item) => setDirectorId(item?.id)}
            />
        </Col>
      </Row> 
      <Row gutter={16} style={{ marginBottom: 20 }}>
        <Col span={4}>
          <label className='attri-name'>截止日期:</label>
        </Col>
        <Col span={8} style={{paddingTop:'1.5px'}}>
          <DatePicker
              placeholder="指定截止日期"
              onChange={(date) => setDeadline(date)}
              value={deadline}
              format="YYYY-MM-DD"
            />
        </Col>
        <Col span={4}>
          <label className='attri-name'>项目状态:</label>
        </Col>
        <Col span={8} className='select-value-status'>
              <StatusSelect 
                placeholder="项目状态"
                value={status}
                valueList={[0,1,2,3]}
                statusList={['待开始','进行中','已完成','已废弃']}
                colorList={['#248dea','#df6005','#0ea913','#979797']}
                onChange={value => setStatus(value)}
                width={120}
              > 
              </StatusSelect> 
        </Col> 
      </Row>

      <Row justify="end" className='op-line'>
          <Button onClick={onClose}>取消</Button>
          <Button type="primary" onClick={handleConfirmAndAddOrUpdateProject} className="confirm-button">
            确认
          </Button>
      </Row>
     

      <Modal
        title="选择需求"
        visible={demandModalVisible}
        onCancel={() => setDemandModalVisible(false)}
        footer={null}
        centered
        className='pick-demend-model'
      >
        <Input
          placeholder="输入编号或内容查找需求"
          value={demandSearch}
          onChange={e => setDemandSearch(e.target.value)}
          className='demand-search-input'
        />
        <div className="demand-list">
          {filteredDemands.map((demand,index) => (
            <div
              key={demand.id+index}
              className="demand-item"
              onClick={() => handleDemandSelect(demand.id)}
            >
              <span className='demand-item-id'>#{demand.id}</span>
              <span>{demand.content.slice(0, 20)}</span> 
            </div>
          ))}
        </div>
      </Modal>
    </Modal>
  );
};

export default AddEdit;

/*

在我的react+antd项目中，帮我补充上述组件。要求css全部放入AddEdit.less中，所有代码放入AddEdit.tsx中。
页面布局和元素如下：
1、整个弹框600宽，高度自适应。
2、第1行为一个Input，代表项目名称
3、第2行为一个textArea，代表项目介绍
4、第3行为关联需求输入框，输入“#”号时，弹出一个浮窗，显示所有的需求列表，需求列表的每一项都由需求编号、需求描述、提报人名称组成。从浮窗中选择好对应的需求时（点击选择），关联需求输入框中的#号变成一个标签样式，内容为#+数字，类似于：#123。每个标签之后跟着一个删除按钮，点击后可以删除。
5、第4行为关联文档，时一个Input，输入文档的链接。
6、第5行为 日期选择器，代表截止日期
7、第6行为提报人展示（当前登录的用户）和 负责人 设置
8、第7行为项目状态，是一个下拉的单选框。

要求：
1、优先使用Ant-D组件
2、使用<Row><Col>进行布局排版

*/